// Shared X / O mark drawn as SVG. Using an SVG (not a Unicode glyph)
// guarantees X and O render at matching stroke widths in every place
// across the game — the board, player cards, VS cards — so the two
// marks read as a visual pair.
//
// NOTE: this is NOT the general "close" × used in dismissible modals.
// That's a UI affordance and stays as a text glyph so it inherits
// platform button typography.
export type XoMark = 'x' | 'o';

type Props = {
  kind: XoMark;
  /** CSS size — applied to both width and height on the SVG.
      Accepts any valid CSS length: '24px', '1.5em', '60%', etc. */
  size?: string | number;
  /** Optional extra classes (e.g. margin / flex-shrink). */
  className?: string;
  /** When false, suppresses the coloured drop-shadow glow. Useful
      in compact UI (player-card rows) where the glow would bleed
      into neighbouring text. */
  glow?: boolean;
};

export default function Mark({ kind, size = '62%', className = '', glow = true }: Props) {
  const stroke = kind === 'x' ? 'var(--xo-x)' : 'var(--xo-o)';
  const glowColor = kind === 'x'
    ? '0 0 10px rgba(34, 211, 238, 0.6)'
    : '0 0 10px rgba(251, 113, 133, 0.55)';
  const s = typeof size === 'number' ? `${size}px` : size;
  return (
    <svg
      viewBox="-50 -50 100 100"
      className={`xo-mark-svg ${className}`}
      style={{
        width: s,
        height: s,
        color: stroke,
        filter: glow ? `drop-shadow(${glowColor})` : undefined,
        display: 'inline-block',
        verticalAlign: 'middle',
      }}
      aria-hidden
    >
      {kind === 'x' ? (
        <>
          <line x1="-30" y1="-30" x2="30" y2="30" stroke="currentColor" strokeWidth="12" strokeLinecap="round" />
          <line x1="-30" y1="30"  x2="30" y2="-30" stroke="currentColor" strokeWidth="12" strokeLinecap="round" />
        </>
      ) : (
        <circle cx="0" cy="0" r="30" fill="none" stroke="currentColor" strokeWidth="12" />
      )}
    </svg>
  );
}
